import { useEffect, useRef } from 'react';
import U2000InputText from '../../../../u2000Core/components/formControls/input/U2000InputText';
import U2000InvalidFeedback from '../../../../u2000Core/components/formControls/feedback/U2000InvalidFeedback';
import type { TFunction } from 'i18next';
import { NS } from '../../../U3157';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { U3157ActiveWorkNature, U3157ImportModelFormRow } from '../../../U3157Service';
import { u3157FormatActiveWorkNatureLabel } from '../../../U3157Service';
import { useU3157Context } from '../../../U3157Context';

interface U3157WorkNatureImportModelBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3157ImportModelFormRow>;
  t: TFunction;
  language: string;
}

function U3157WorkNatureImportModelBody({ formStateHandlers, t, language }: U3157WorkNatureImportModelBodyProps) {
  const { initLU } = useU3157Context();
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const activeWorkNatures = initLU.activeWorkNatures ?? [];

  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const selectedIds = new Set(data?.workNatureIds ?? []);
  const catalogIds = activeWorkNatures.map((nature) => nature.id);
  const allSelected = catalogIds.length > 0 && catalogIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && selectedIds.size < catalogIds.length;

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = someSelected;
  }, [someSelected]);

  if (!data) return null;

  const setWorkNatureIds = (next: string[]) => {
    formStateHandlers.setData({ ...data, workNatureIds: [...next].sort() });
  };

  const handleSelectAllChange = () => {
    if (readonly) return;
    if (allSelected || someSelected) {
      setWorkNatureIds([]);
    } else {
      setWorkNatureIds([...catalogIds]);
    }
  };

  const handleNatureChange = (natureId: string, checked: boolean) => {
    if (readonly) return;
    const next = new Set(selectedIds);
    if (checked) next.add(natureId);
    else next.delete(natureId);
    setWorkNatureIds(Array.from(next));
  };

  const renderNatureCheckbox = (nature: U3157ActiveWorkNature) => (
    <label key={nature.id} className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
      <input
        type="checkbox"
        checked={selectedIds.has(nature.id)}
        onChange={(e) => handleNatureChange(nature.id, e.target.checked)}
        disabled={readonly}
        className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span>{u3157FormatActiveWorkNatureLabel(nature, language)}</span>
    </label>
  );

  return (
    <div className="space-y-8">
      <div>
        <U2000InputText
          formStateHandlers={formStateHandlers as never}
          fieldName="name"
          label={t('lblMandateTypeCode', { ns: NS })}
          required
          maxLength={255}
          readonly={readonly}
        />
        <p className="mt-1 text-sm text-gray-600">{t('msgImportModelPurpose', { ns: NS })}</p>
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2">
          <span className="text-sm font-medium text-gray-700">{t('lblWorkNaturesSection', { ns: NS })}</span>
          {!readonly && activeWorkNatures.length > 0 && (
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAllChange}
                className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>{t('lblSelectAllWorkNatures', { ns: NS })}</span>
            </label>
          )}
        </div>

        {activeWorkNatures.length === 0 ? (
          <p className="text-sm text-gray-500">{t('msgNoActiveWorkNatures', { ns: NS })}</p>
        ) : readonly && selectedIds.size === 0 ? (
          <p className="text-sm text-gray-500">{t('msgNoWorkNaturesSelected', { ns: NS })}</p>
        ) : (
          <div
            className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            role="group"
            aria-label={t('lblWorkNaturesSection', { ns: NS })}
          >
            {(readonly ? activeWorkNatures.filter((nature) => selectedIds.has(nature.id)) : activeWorkNatures).map(
              renderNatureCheckbox,
            )}
          </div>
        )}

        <U2000InvalidFeedback formStateHandlers={formStateHandlers as never} fieldName="workNatureIds" />
      </div>
    </div>
  );
}

export default U3157WorkNatureImportModelBody;
